import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { toast } from "sonner";
import {
  Wallet,
  TrendingUp,
  Clock,
  CheckCircle2,
  Download,
  FileText,
  RefreshCw,
  Banknote,
  Users,
  AlertTriangle,
} from "lucide-react";

const fmt = (n) => `K ${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const BUCKETS = [
  { key: "held", label: "Held (no result yet)", icon: Clock, color: "bg-gray-100 text-gray-700" },
  { key: "awaiting_confirmation", label: "Awaiting Tenant Confirmation", icon: AlertTriangle, color: "bg-amber-100 text-amber-700" },
  { key: "ready_for_payout", label: "Ready for Payout", icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
  { key: "paid_out", label: "Paid Out", icon: Banknote, color: "bg-blue-100 text-blue-700" },
  { key: "cancelled", label: "Cancelled", icon: AlertTriangle, color: "bg-red-100 text-red-700" },
];

const Kpi = ({ icon: Icon, label, value, color, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="pt-4">
      <div className="flex items-center gap-3">
        <div className={`${color} text-white p-2 rounded-lg`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xl font-bold">{value}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const BrokerWallet = () => {
  const { user } = useAuth();
  const isAdminLike = ["admin", "accounts", "ceo"].includes(user?.role);
  const isBroker = user?.role === "broker";

  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brokers, setBrokers] = useState([]);
  const [selectedBrokerId, setSelectedBrokerId] = useState("");
  const [downloading, setDownloading] = useState(false);

  const fetchWallet = useCallback(async () => {
    setLoading(true);
    try {
      const url = isAdminLike && selectedBrokerId
        ? `${API}/broker/wallet?broker_id=${selectedBrokerId}`
        : `${API}/broker/wallet`;
      const res = await axios.get(url);
      setWallet(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load wallet");
    } finally {
      setLoading(false);
    }
  }, [isAdminLike, selectedBrokerId]);

  const fetchBrokerList = useCallback(async () => {
    if (!isAdminLike) return;
    try {
      const res = await axios.get(`${API}/broker/wallet/brokers`);
      setBrokers(res.data || []);
    } catch {
      /* non-critical */
    }
  }, [isAdminLike]);

  useEffect(() => {
    fetchWallet();
  }, [fetchWallet]);

  useEffect(() => {
    fetchBrokerList();
  }, [fetchBrokerList]);

  const downloadStatement = async (fmtType) => {
    setDownloading(true);
    try {
      const brokerQuery = isAdminLike && selectedBrokerId ? `?broker_id=${selectedBrokerId}` : "";
      const url = `${API}/broker/wallet/statement.${fmtType}${brokerQuery}`;
      const response = await axios.get(url, { responseType: "blob" });

      // Extract filename from Content-Disposition or fallback
      const cd = response.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const filename = m ? m[1] : `broker_wallet.${fmtType}`;

      const blob = new Blob([response.data], { type: response.headers["content-type"] });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${fmtType.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error("Failed to download statement");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="broker-wallet-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdminLike && !isBroker) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          You don&apos;t have permission to view the Broker Wallet.
        </CardContent>
      </Card>
    );
  }

  const s = wallet?.summary || {};
  const buckets = wallet?.buckets || {};

  return (
    <div className="space-y-6" data-testid="broker-wallet">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="w-6 h-6" /> Broker Wallet
          </h1>
          <p className="text-muted-foreground text-sm">
            Viewing: <strong>{wallet?.viewer || "—"}</strong>
          </p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          {isAdminLike && brokers.length > 0 && (
            <select
              value={selectedBrokerId}
              onChange={(e) => setSelectedBrokerId(e.target.value)}
              className="px-3 py-2 border rounded-md text-sm"
              data-testid="wallet-broker-select"
            >
              <option value="">All Brokers</option>
              {brokers.map((b) => (
                <option key={b.user_id} value={b.user_id}>{b.name}</option>
              ))}
            </select>
          )}
          <Button variant="outline" onClick={fetchWallet} data-testid="wallet-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadStatement("csv")}
            disabled={downloading}
            data-testid="wallet-download-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" /> CSV
          </Button>
          <Button
            onClick={() => downloadStatement("pdf")}
            disabled={downloading}
            data-testid="wallet-download-pdf-btn"
          >
            <FileText className="w-4 h-4 mr-2" /> PDF Statement
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={TrendingUp} label="Total Deposited" value={fmt(s.total_deposited)} color="bg-blue-500" testId="kpi-total-deposited" />
        <Kpi icon={Clock} label="Currently Held" value={fmt(s.held_amount + s.awaiting_confirmation_amount)} color="bg-amber-500" testId="kpi-held" />
        <Kpi icon={CheckCircle2} label="Ready for Payout" value={fmt(s.pending_payout_amount)} color="bg-emerald-500" testId="kpi-ready" />
        {isAdminLike ? (
          <Kpi icon={Banknote} label="Commission Earned" value={fmt(s.total_commission)} color="bg-purple-500" testId="kpi-commission" />
        ) : (
          <Kpi icon={Banknote} label="Paid Out to You" value={fmt(s.total_paid_to_broker)} color="bg-purple-500" testId="kpi-paid-to-me" />
        )}
      </div>

      {/* Balance breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Balance Breakdown by Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {BUCKETS.map(({ key, label, icon: Icon, color }) => {
              const bucket = buckets[key] || { count: 0, transactions: [] };
              const sum = (bucket.transactions || []).reduce(
                (a, t) => a + (key === "paid_out" || key === "ready_for_payout" ? (t.payout_amount || 0) : (t.deposit_amount || 0)),
                0
              );
              return (
                <div key={key} className={`p-3 rounded ${color}`} data-testid={`bucket-${key}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4" />
                    <span className="text-xs font-medium">{label}</span>
                  </div>
                  <div className="text-lg font-bold">{fmt(sum)}</div>
                  <div className="text-xs opacity-75">{bucket.count} transaction{bucket.count === 1 ? "" : "s"}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Per-bucket transaction tables */}
      {BUCKETS.map(({ key, label }) => {
        const bucket = buckets[key];
        if (!bucket || bucket.count === 0) return null;
        return (
          <Card key={key} data-testid={`bucket-section-${key}`}>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <span>{label}</span>
                <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{bucket.count}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b">
                    <tr>
                      <th className="text-left p-2">Date</th>
                      {isAdminLike && <th className="text-left p-2">Broker</th>}
                      <th className="text-left p-2">Tenant</th>
                      <th className="text-right p-2">Deposit</th>
                      <th className="text-right p-2">Commission</th>
                      <th className="text-right p-2">Payout (90%)</th>
                      <th className="text-left p-2">Recipient</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bucket.transactions.map((tx) => (
                      <tr key={tx.transaction_id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="p-2 text-xs">{fmtDate(tx.created_at)}</td>
                        {isAdminLike && <td className="p-2 text-xs">{tx.broker_name}</td>}
                        <td className="p-2 text-xs">{tx.tenant_name}</td>
                        <td className="p-2 text-right">{fmt(tx.deposit_amount)}</td>
                        <td className="p-2 text-right text-purple-700">{fmt(tx.commission_amount)}</td>
                        <td className="p-2 text-right font-semibold">{fmt(tx.payout_amount)}</td>
                        <td className="p-2 text-xs capitalize">
                          {tx.payout_recipient || (tx.status === "confirmed_success" ? "broker" : tx.status === "confirmed_failed" ? "tenant" : "—")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {wallet?.summary?.total_transactions === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground" data-testid="wallet-empty">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>No transactions yet. Your wallet will populate as deposits are recorded.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BrokerWallet;
